import { Component, computed, inject, input, output, SimpleChanges, ViewChild } from '@angular/core';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { QaiDialogComponent } from '@qai/ui';
import { IConfirmActionPayload } from 'src/app/interfaces/self-serve/requests/iconfirm-action-payload';
import { ISelfAuthoringPrefillData } from 'src/app/interfaces/self-serve/iself-authoring-prefill-data';
import { SimulationGenerationPollingService } from 'src/app/services/self-serve/simulation-generation-polling.service';
import { SimulationTypeEnum } from 'src/app/utils/enums/self-serve/simulation-type.enum';
import { SelfAuthoringSimulationFormComponent } from './self-authoring-simulation-form/self-authoring-simulation-form.component';
import { SimulationGenerationModalComponent } from './simulation-generation-modal/simulation-generation-modal.component';
import { LeaveConfirmationModalComponent } from '../leave-confirmation-modal/leave-confirmation-modal.component';
@Component({
  selector: 'self-authoring-simulation-generation-modal',
  standalone: true,
  templateUrl: './self-authoring-simulation-generation-modal.component.html',
  styleUrls: ['./self-authoring-simulation-generation-modal.component.scss'],
  imports: [
    QaiDialogComponent,
    TranslocoModule,
    SelfAuthoringSimulationFormComponent,
    SimulationGenerationModalComponent,
    LeaveConfirmationModalComponent,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class SelfAuthoringSimulationGenerationModalComponent {
  private readonly pollingService = inject(SimulationGenerationPollingService);

  @ViewChild('selfAuthoringCreateDialog') qaiDialogRef?: QaiDialogComponent;
  @ViewChild(SelfAuthoringSimulationFormComponent) formRef?: SelfAuthoringSimulationFormComponent;

  open = input(false);
  activeCallUuid = input<string | undefined>(undefined);
  moduleUuid = input<string | undefined>(undefined);
  networkRequestInProgress = input(false);
  errorStatus = input<number>();
  isFilesExceedingTokens = input(false);
  prefillData = input<ISelfAuthoringPrefillData | undefined>(undefined);
  retryInProgress = input(false);

  // SR-19909: which type of simulation opened this modal. Defaults to ROLE_PLAY so existing
  // callers/behavior are unaffected. Knowledge Check reuses the Role Play form with its own
  // title and without the scenario and reference documents fields.
  // TODO(Knowledge Check build-out ticket): switch in remaining KC-specific fields, copy,
  // and validation.
  simulationType = input<SimulationTypeEnum>(SimulationTypeEnum.ROLE_PLAY);
  isKnowledgeCheck = computed(() => this.simulationType() === SimulationTypeEnum.KNOWLEDGE_CHECK);

  confirmAction = output<IConfirmActionPayload>();
  closeModal = output<void>();
  generationClosed = output<void>();
  backToFormWithPrefill = output<void>();
  retryClicked = output<void>();
  formShowLeaveConfirmation = false;
  formIsLeaving = false;

  get isProcessing(): boolean {
    return !!this.activeCallUuid();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      if (this.open()) {
        this.formShowLeaveConfirmation = false;
        this.formIsLeaving = false;
        this.qaiDialogRef?.showModal();
      } else {
        this.qaiDialogRef?.close();
      }
    }
  }

  handleDialogClosed(): void {
    if (this.isProcessing || this.networkRequestInProgress()) {
      this.suppressCurrentGenerationResult();
      this.generationClosed.emit();
      return;
    }
    this.formRef?.handleCloseClick();
  }

  /** Shared by both exit paths out of the generation view: "Back" (failure) and the review modal closing (completed). */
  handleGenerationExit(): void {
    this.suppressCurrentGenerationResult();
    this.generationClosed.emit();
  }

  /** The 20-minute polling cap was hit — the record isn't terminal, so there's nothing to suppress/dismiss, just close. */
  handlePollingTimedOut(): void {
    this.generationClosed.emit();
  }

  private suppressCurrentGenerationResult(): void {
    const callUuid = this.activeCallUuid();
    if (callUuid && this.pollingService.isTerminalStatus(callUuid)) {
      this.pollingService.dismiss(callUuid);
    }
  }
}
